/**
 * SMASH-MAN (Discman) — disc loading + spin.
 *
 * Additive to the media player's existing transport script (play/pause/seek/
 * volume by element ID). This file: loads the picked disc into the shared
 * <audio>, swaps the holographic CD art, drives the LCD + spin, handles
 * prev/next, and auto-advances at end of track.
 */
(function () {
  'use strict';

  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  function initDm(dm) {
    if (dm._dmReady) return;
    dm._dmReady = true;

    var audio = dm.querySelector('.st-media-player__audio');
    var discs = Array.prototype.slice.call(dm.querySelectorAll('[data-track]'));
    var cover = dm.querySelector('[data-dm-cover]');
    var trackNo = dm.querySelector('[data-dm-trackno]');
    var nowText = dm.querySelector('[data-dm-nowtext]');
    var prevBtn = dm.querySelector('[data-dm-prev]');
    var nextBtn = dm.querySelector('[data-dm-next]');
    var download = dm.querySelector('[data-eject]');
    var cd = dm.querySelector('[data-dm-disc]');
    if (!audio) return;

    var current = -1;

    // ---- eased disc spin (ramps up on play, coasts to a stop on pause) ----
    var spinAngle = 0, spinVel = 0, spinTarget = 0, spinRaf = null;
    var SPIN_MAX = 19;        // deg/frame at 60fps ≈ a fast CD
    var SPIN_ACCEL = 0.045;   // normal ease toward target
    var SOFT_ACCEL = 0.018;   // gentle ramp used right after a disc change
    var spinAccel = SPIN_ACCEL;
    var softRamp = false;     // true while a freshly-loaded disc eases up to speed
    var pendingSwap = null;   // a disc waiting to load once the current one stops
    function spinLoop() {
      // ease velocity toward target (up when playing, down to 0 when paused)
      spinVel += (spinTarget - spinVel) * spinAccel;
      spinAngle = (spinAngle + spinVel) % 360;
      if (cd) cd.style.transform = 'rotate(' + spinAngle + 'deg)';
      // once a soft-started disc is up to speed, return to normal responsiveness
      if (softRamp && spinVel > SPIN_MAX * 0.85) { softRamp = false; spinAccel = SPIN_ACCEL; }
      // disc change: once the old disc has fully settled, swap + softly spin up
      if (pendingSwap && spinTarget === 0 && Math.abs(spinVel) < 0.25) {
        var ps = pendingSwap; pendingSwap = null;
        softRamp = true;                  // next spinUp ramps up gently, not instantly
        applyDisc(ps.idx, ps.autoplay);   // applyDisc → audio 'play' → spinUp()
      }
      if (spinTarget > 0 || Math.abs(spinVel) > 0.04 || pendingSwap) {
        spinRaf = requestAnimationFrame(spinLoop);
      } else {
        spinVel = 0;
        spinRaf = null;
      }
    }
    function spinUp() { spinAccel = softRamp ? SOFT_ACCEL : SPIN_ACCEL; spinTarget = SPIN_MAX; if (!spinRaf) spinRaf = requestAnimationFrame(spinLoop); }
    function spinDown() { spinAccel = SPIN_ACCEL; softRamp = false; spinTarget = 0; if (!spinRaf) spinRaf = requestAnimationFrame(spinLoop); }

    // Swap the loaded disc immediately (art, src, LCD, highlight) and optionally play.
    function applyDisc(idx, autoplay) {
      if (idx < 0 || idx >= discs.length) return;
      var disc = discs[idx];
      var changed = (idx !== current);
      current = idx;
      if (changed) {
        audio.src = disc.getAttribute('data-url') || '';
        try { audio.load(); } catch (e) {}
        if (download && audio.src) download.setAttribute('href', audio.src);
      }
      // CD art
      var art = disc.getAttribute('data-cover') || '';
      if (cover) cover.style.backgroundImage = art ? ('url(' + art + ')') : 'none';
      // shelf highlight
      discs.forEach(function (d) { d.classList.remove('is-loaded'); });
      disc.classList.add('is-loaded');
      // LCD readout
      if (trackNo) trackNo.textContent = 'TR ' + pad2(idx + 1);
      if (nowText) {
        var ti = disc.getAttribute('data-title') || 'Untitled';
        var ar = disc.getAttribute('data-artist') || '';
        var label = ar ? (ti + '  —  ' + ar) : ti;
        // duplicate (label + spacer) so the -50% marquee loops seamlessly
        var unit = label + '  ◆  ';
        nowText.textContent = unit + unit;
      }
      if (autoplay) { var p = audio.play(); if (p && p.catch) p.catch(function () {}); }
    }

    // Public loader: if a disc is already spinning, ease it to a stop, then swap
    // and spin the new one up. Otherwise load instantly.
    function loadDisc(idx, autoplay) {
      if (idx < 0 || idx >= discs.length) return;
      var spinning = !audio.paused || Math.abs(spinVel) > 1;
      if (idx !== current && spinning) {
        pendingSwap = { idx: idx, autoplay: autoplay };
        audio.pause();   // silence the outgoing track while the disc winds down
        spinDown();      // spinLoop applies the swap once it nearly stops
      } else {
        applyDisc(idx, autoplay);
      }
    }

    function restart() {
      try { audio.currentTime = 0; } catch (e) {}
      var p = audio.play(); if (p && p.catch) p.catch(function () {});
    }
    function step(dir) {
      if (!discs.length) return;
      if (discs.length === 1) { restart(); return; }       // one disc → restart it
      var i = dir < 0
        ? (current <= 0 ? discs.length - 1 : current - 1)
        : (current + 1) % discs.length;
      loadDisc(i, true);
    }

    discs.forEach(function (disc, i) {
      disc.addEventListener('click', function () { loadDisc(i, true); });
    });
    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

    // spin + LCD state
    audio.addEventListener('play', function () { dm.classList.add('is-playing'); spinUp(); });
    audio.addEventListener('playing', function () { dm.classList.add('is-playing'); spinUp(); });
    audio.addEventListener('pause', function () { dm.classList.remove('is-playing'); spinDown(); });
    audio.addEventListener('ended', function () {
      dm.classList.remove('is-playing');
      spinDown();
      if (current + 1 < discs.length) loadDisc(current + 1, true);
    });

    // preload the first disc (paused) so play works immediately
    if (discs.length) loadDisc(0, false);
    else if (nowText) nowText.textContent = 'NO DISC';
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('.st-dm'), initDm);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);

  var pending = false;
  var mo = new MutationObserver(function () {
    if (pending) return;
    pending = true;
    setTimeout(function () { pending = false; initAll(); }, 60);
  });
  if (document.body) mo.observe(document.body, { childList: true, subtree: true });

  window.SmashMan = { init: initAll };
})();
